/**
 * Rename Consistency page — A2F-815; source-grouped in A2F-872.
 *
 * Surfaces identifier renames detected between the focused submission
 * and its predecessor sequence, clustered by **source identifier**. One
 * cluster per old id lists every new id it became (the variants, each
 * with its own application count + locations) and — the actionable bit —
 * where the old id still appears, so the user can fix the stragglers.
 *
 * Out of scope here: dismissal flow (A2F-874/875), scope filters,
 * descriptive renames + LLM referent-check (re-scoped A2F-816).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  EyeOff,
  LoaderCircle,
  RefreshCw,
  Search,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useProject } from '@/lib/ProjectContext';
import { useDashboardCache } from '@/lib/DashboardCacheContext';
import { usePermissions } from '@/lib/usePermissions';
import { useScanStatus } from '@/lib/ScanStatusContext';
import { useScanTask } from '@/lib/useScanTask';
import {
  invalidateConsistency,
  onConsistencyInvalidated,
} from '@/lib/consistencyInvalidation';
import { useSubmissionFocus } from '@/lib/SubmissionFocusContext';
import { Telemetry } from '@/lib/telemetry';
import { formatInstant } from '@/lib/formatInstant';
import { useDisplayTimezone } from '@/lib/useDisplayTimezone';
import { ScanProgress } from '@/components/shared/ScanProgress';
import { SummaryStatCard } from '@/components/shared/SummaryStatCard';
import { formatSectionLabel } from '@/lib/formatSectionLabel';
import type {
  RenameConsistencyCluster,
  RenameConsistencyScanResponse,
  RenameLocation,
} from '@/types/api';

type UIState = 'loading' | 'landing' | 'scanning' | 'results' | 'gated';
type StatusFilter = 'all' | 'consistent' | 'inconsistent';

export function RenameConsistencyPage() {
  const tz = useDisplayTimezone();
  const { activeProject } = useProject();
  const { submissionFocusId: submissionId, submissions } = useSubmissionFocus();
  const { invalidate: invalidateDashboard } = useDashboardCache();
  const { canRunScans, canDismissFindings } = usePermissions();
  const scanStatus = useScanStatus();
  const projectId = activeProject?.id;

  const [uiState, setUiState] = useState<UIState>('loading');
  const [data, setData] = useState<RenameConsistencyScanResponse | null>(null);
  const [error, setError] = useState('');
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  // Mirrors the Abbreviations page: dismissed clusters hidden by default,
  // revealed via the "Hide dismissed (N)" toggle in the filter bar.
  const [hideDismissed, setHideDismissed] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const scanBusy = scanStatus.isScanning('rename-consistency');

  const onScanComplete = useCallback(() => {
    invalidateDashboard();
    if (projectId) invalidateConsistency(projectId);
  }, [invalidateDashboard, projectId]);

  const {
    taskState,
    startTask,
    cancel: cancelScan,
    reset: resetTask,
    result: scanResult,
  } = useScanTask<RenameConsistencyScanResponse>('rename-consistency', onScanComplete);

  // ---- Data load ----------------------------------------------------------

  const loadCached = useCallback(() => {
    if (!projectId) return;
    setUiState('loading');
    setError('');
    api.consistency.get(projectId, submissionId ?? undefined)
      .then((result) => {
        const entry = result.perFeature?.renameConsistencyInconsistent;
        const applicable = entry?.applicable ?? true;
        if (!applicable) {
          setData(null);
          setUiState('gated');
          return;
        }
        const val = entry?.value as RenameConsistencyScanResponse | null | undefined;
        if (val) {
          setData(val);
          setUiState('results');
          return;
        }
        // Applicable but un-scanned. Check for an in-flight task.
        api.tasks.findRunning(projectId, 'rename_consistency_scan')
          .then((task) => {
            setUiState('scanning');
            startTask(task.taskId);
          })
          .catch(() => setUiState('landing'));
      })
      .catch(() => setUiState('landing'));
  }, [projectId, submissionId, startTask]);

  useEffect(() => {
    loadCached();
  }, [loadCached]);

  // Refetch when any scan invalidates consistency for this project.
  useEffect(() => {
    return onConsistencyInvalidated((pid) => {
      if (pid === projectId) loadCached();
    });
  }, [projectId, loadCached]);

  // Scan-task completion handler.
  useEffect(() => {
    if (!scanResult) return;
    scanStatus.endScan('rename-consistency');
    setData(scanResult);
    setUiState('results');
    Telemetry.reviewResultsViewed({
      feature: 'rename-consistency',
      finding_count: scanResult.renames?.length ?? 0,
      stale_files: scanResult.staleFiles ?? 0,
    });
  }, [scanResult]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Scan triggers ------------------------------------------------------

  const startScan = useCallback(async () => {
    if (!projectId) return;
    if (!scanStatus.startScan('rename-consistency')) return;
    setUiState('scanning');
    setError('');
    resetTask();
    try {
      // PR #404 review: pass focused submissionId, not overlayId.
      // Overlay-id is null for clean subs (no superseded files), which
      // silently produced empty results for the common path.
      const { taskId } = await api.renameConsistency.scan(
        projectId,
        submissionId ?? undefined,
      );
      startTask(taskId);
    } catch (err: unknown) {
      scanStatus.endScan('rename-consistency');
      const msg = err instanceof Error ? err.message : 'Failed to start scan';
      setError(msg);
      setUiState(data ? 'results' : 'landing');
    }
  }, [projectId, submissionId, scanStatus, resetTask, startTask, data]);

  const rescan = useCallback(() => {
    if (!projectId) return;
    Telemetry.reviewCacheCleared({ feature: 'rename-consistency' });
    api.renameConsistency.clearCache(projectId).then(() => startScan());
  }, [projectId, startScan]);

  // ---- Dismiss / undismiss ------------------------------------------------
  // Cluster-level, keyed on the source identifier (A2F-872/874). Optimistic
  // local-state update mirrors the Abbreviations page; the dashboard tile
  // refreshes via invalidateDashboard.

  const handleDismiss = useCallback(async (sourceId: string, reason: string) => {
    if (!projectId) return;
    await api.renameConsistency.dismiss(projectId, sourceId, reason);
    Telemetry.findingDismissed({
      feature: 'rename-consistency',
      finding_id: sourceId,
      has_rationale: reason.trim().length >= 10,
    });
    setData((prev) => prev && {
      ...prev,
      renames: prev.renames.map((r) =>
        r.oldId === sourceId
          ? { ...r, dismissed: true, dismissReason: reason || '' }
          : r,
      ),
    });
    invalidateDashboard();
  }, [projectId, invalidateDashboard]);

  const handleUndismiss = useCallback(async (sourceId: string) => {
    if (!projectId) return;
    await api.renameConsistency.undismiss(projectId, sourceId);
    Telemetry.findingUndismissed({
      feature: 'rename-consistency',
      finding_id: sourceId,
    });
    setData((prev) => prev && {
      ...prev,
      renames: prev.renames.map((r) =>
        r.oldId === sourceId
          ? { ...r, dismissed: false, dismissReason: null }
          : r,
      ),
    });
    invalidateDashboard();
  }, [projectId, invalidateDashboard]);

  // ---- Per-instance dismiss (A2F-898) -------------------------------------
  // Optimistic local update mirrors the backend merge: flag the matched
  // stale location, then recompute remainingOldCount / isInconsistent from
  // the non-dismissed stragglers so the summary + dashboard stay in agreement.

  const applyInstance = useCallback((
    sourceId: string, loc: RenameLocation, patch: Partial<RenameLocation>,
  ) => {
    setData((prev) => prev && {
      ...prev,
      renames: prev.renames.map((r) => {
        if (r.oldId !== sourceId) return r;
        const oldIdLocations = r.oldIdLocations.map((l) =>
          l.documentId === loc.documentId
            && l.contextHash === loc.contextHash
            && l.ectdSection === loc.ectdSection
            && l.occurrence === loc.occurrence
            ? { ...l, ...patch } : l,
        );
        const remaining = oldIdLocations.filter((l) => !l.dismissed).length;
        return {
          ...r,
          oldIdLocations,
          remainingOldCount: remaining,
          isInconsistent: remaining > 0,
        };
      }),
    });
  }, []);

  const handleDismissInstance = useCallback(
    async (sourceId: string, loc: RenameLocation, reason: string) => {
      if (!projectId) return;
      await api.renameConsistency.dismissInstance(projectId, {
        sourceId,
        documentId: loc.documentId,
        sectionPath: loc.ectdSection ?? '',
        contextHash: loc.contextHash ?? '',
        occurrence: loc.occurrence ?? 0,
      }, reason);
      Telemetry.findingDismissed({
        feature: 'rename-consistency',
        finding_id: `${sourceId}:${loc.contextHash ?? ''}`,
        has_rationale: reason.trim().length >= 10,
      });
      applyInstance(sourceId, loc, { dismissed: true, dismissReason: reason || '' });
      invalidateDashboard();
    }, [projectId, applyInstance, invalidateDashboard]);

  const handleUndismissInstance = useCallback(
    async (sourceId: string, loc: RenameLocation) => {
      if (!projectId) return;
      await api.renameConsistency.undismissInstance(projectId, {
        sourceId,
        documentId: loc.documentId,
        sectionPath: loc.ectdSection ?? '',
        contextHash: loc.contextHash ?? '',
        occurrence: loc.occurrence ?? 0,
      });
      Telemetry.findingUndismissed({
        feature: 'rename-consistency',
        finding_id: `${sourceId}:${loc.contextHash ?? ''}`,
      });
      applyInstance(sourceId, loc, { dismissed: false, dismissReason: null });
      invalidateDashboard();
    }, [projectId, applyInstance, invalidateDashboard]);

  // ---- Derived view -------------------------------------------------------

  // A2F-982: drop 0-application variants — they're noise on the cluster card
  // (truncation artifacts, document-content typos that survive the A2F-970
  // extractor fix, or genuinely just-introduced renames where the new id
  // only appears in the introducing paragraph). A cluster whose ONLY
  // variants are 0-application is hidden entirely. Underlying scan data
  // (``data.renames``) is unchanged — this is purely presentation.
  //
  // ``totalApplicationCount`` stays correct under this filter because
  // 0-application variants contribute 0 to the sum; only ``variantCount``
  // needs re-derivation from the filtered list.
  const appliedRenames = useMemo<RenameConsistencyCluster[]>(() => {
    if (!data) return [];
    return data.renames
      .map((c): RenameConsistencyCluster | null => {
        const applied = c.variants.filter((v) => v.applicationCount > 0);
        if (applied.length === 0) return null;
        return { ...c, variants: applied, variantCount: applied.length };
      })
      .filter((c): c is RenameConsistencyCluster => c !== null);
  }, [data]);

  // Filter the cluster list by status + free-text search across the source
  // id, every variant new id, and each variant's introducing-file metadata.
  // Service-side ordering (total application count desc, tiebreak
  // remaining_old) is preserved.
  const visibleClusters = useMemo<RenameConsistencyCluster[]>(() => {
    let out: RenameConsistencyCluster[] = appliedRenames;
    if (hideDismissed) {
      out = out.filter((r) => !r.dismissed);
    }
    if (statusFilter === 'consistent') {
      out = out.filter((r) => !r.isInconsistent);
    } else if (statusFilter === 'inconsistent') {
      out = out.filter((r) => r.isInconsistent);
    }
    const q = searchText.trim().toLowerCase();
    if (q) {
      out = out.filter((r) => {
        const hay = [
          r.oldId,
          ...r.variants.flatMap((v) => [
            v.newId,
            v.introducedIn.fileName,
            v.introducedIn.ectdSection ?? '',
            v.introducedIn.headingPath ?? '',
          ]),
        ].join(' ').toLowerCase();
        return hay.includes(q);
      });
    }
    return out;
  }, [appliedRenames, statusFilter, searchText, hideDismissed]);

  const dismissedCount = useMemo(
    () => appliedRenames.filter((r) => r.dismissed).length,
    [appliedRenames],
  );

  const summary = useMemo(() => {
    // Use appliedRenames (post-A2F-982 filter) so summary counts match what
    // the user sees in the cluster list — clusters with only 0-application
    // variants are hidden from the UI, so they must not bump the total.
    const renames = appliedRenames;
    // Counts reflect post-dismissal state: a dismissed cluster is neither
    // "inconsistent" nor counted toward the total the user must act on,
    // matching the dashboard tile's !dismissed filter and the aggregate.
    const active = renames.filter((r) => !r.dismissed);
    const inconsistent = active.filter((r) => r.isInconsistent).length;
    return {
      total: active.length,
      consistent: active.length - inconsistent,
      inconsistent,
      // `documentMap` is filename → doc_id over the location-matching doc
      // set — i.e. the docs we extracted text from to count applications
      // and stale matches. That's the right "Files Scanned" denominator
      // for this Review feature.
      filesScanned: data?.documentMap ? Object.keys(data.documentMap).length : 0,
    };
  }, [appliedRenames, data]);

  const focusedSubLabel = useMemo(() => {
    if (!submissionId) return null;
    const sub = submissions.find((s) => s.id === submissionId);
    return sub?.sequenceNumber ?? null;
  }, [submissionId, submissions]);

  const previousSubLabel = useMemo(() => {
    const prevId = data?.previousSubmissionId;
    if (!prevId) return null;
    const sub = submissions.find((s) => s.id === prevId);
    return sub?.sequenceNumber ?? null;
  }, [data?.previousSubmissionId, submissions]);

  const toggleExpanded = (clusterId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(clusterId)) next.delete(clusterId);
      else next.add(clusterId);
      return next;
    });
  };

  // ---- Render -------------------------------------------------------------

  if (!activeProject) {
    return (
      <div className="max-w-5xl mx-auto py-16 text-center">
        <p className="text-slate-500">
          No project selected. Create or select a project in Settings.
        </p>
      </div>
    );
  }

  // The status-filter handler used by clickable summary tiles. Acts as a
  // toggle: clicking the active status resets to "all".
  const handleStatusOnlyFilter = useCallback((status: string) => {
    setStatusFilter((prev) => (prev === status ? 'all' : (status as StatusFilter)));
  }, []);

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Summary cards at top — matches the other 5 Review pages. Only
          shown once a scan has produced data; landing/loading/scanning
          states omit the cards. */}
      {uiState === 'results' && data && (
        <div className="grid grid-cols-4 gap-4">
          <SummaryStatCard
            label="Total Renames"
            value={summary.total}
            color="slate"
            statusValue="all"
            activeStatus={statusFilter}
            onFilter={handleStatusOnlyFilter}
          />
          <SummaryStatCard
            label="Consistent"
            value={summary.consistent}
            color="green"
            statusValue="consistent"
            activeStatus={statusFilter}
            onFilter={handleStatusOnlyFilter}
          />
          <SummaryStatCard
            label="Inconsistent"
            value={summary.inconsistent}
            color="orange"
            statusValue="inconsistent"
            activeStatus={statusFilter}
            onFilter={handleStatusOnlyFilter}
          />
          <SummaryStatCard
            label="Files Scanned"
            value={summary.filesScanned}
            color="slate"
          />
        </div>
      )}

      {/* Filter bar — search input + status dropdown, shown in results
          state alongside the rescan button. Matches the other Review pages. */}
      {uiState === 'results' && data && (
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search identifiers, files, sections…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange bg-white"
          >
            <option value="all">All Statuses</option>
            <option value="consistent">Consistent</option>
            <option value="inconsistent">Inconsistent</option>
          </select>
          {dismissedCount > 0 && (
            <label className="inline-flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={hideDismissed}
                onChange={(e) => setHideDismissed(e.target.checked)}
                className="rounded border-slate-300 text-brand-orange focus:ring-brand-orange/30"
              />
              <EyeOff className="w-3.5 h-3.5 text-slate-400" />
              Hide dismissed ({dismissedCount})
            </label>
          )}
          <div className="ml-auto">
            {(() => {
              const isGated = false; // results state is always non-gated
              const disabled = !canRunScans || scanBusy || isGated;
              return (
                <button
                  onClick={rescan}
                  disabled={disabled}
                  className={`inline-flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {scanBusy ? (
                    <><LoaderCircle className="w-4 h-4 animate-spin" /> Scanning&hellip;</>
                  ) : (
                    <><RefreshCw className="w-4 h-4" /> Rescan</>
                  )}
                </button>
              );
            })()}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-brand-charcoal">
              Renames
            </h2>
            <p className="text-[11px] font-semibold text-brand-orange uppercase tracking-widest mt-1">
              Identifier renames across submissions
            </p>
            {previousSubLabel && focusedSubLabel && (
              <p className="text-sm text-slate-500 mt-2">
                Submission {previousSubLabel} → {focusedSubLabel}
                {data?.scannedAt && (
                  <>
                    {' '}&middot; Scanned{' '}
                    {formatInstant(data.scannedAt, tz)}
                  </>
                )}
              </p>
            )}
          </div>
          {/* Header Scan button — shown in non-results states only.
              In ``results``, the rescan affordance lives in the filter
              bar above, matching the other Review pages. Disabled with
              an explanatory tooltip when gated (first-sequence sub /
              no focus) or already scanning. */}
          {uiState !== 'results' && (() => {
            const isGated = uiState === 'gated';
            const disabledReason = !canRunScans
              ? 'Requires Contributor role'
              : isGated
                ? 'Focus a submission with a previous sequence to enable scanning'
                : undefined;
            const disabled = !canRunScans || scanBusy || isGated;
            return (
              <button
                onClick={startScan}
                disabled={disabled}
                title={disabledReason}
                className={`inline-flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {scanBusy ? (
                  <><LoaderCircle className="w-4 h-4 animate-spin" /> Scanning&hellip;</>
                ) : (
                  <><RefreshCw className="w-4 h-4" /> Scan</>
                )}
              </button>
            );
          })()}
        </div>

        {/* Error banner */}
        {error && (
          <div className="mt-4 px-4 py-3 bg-rose-50 border border-rose-200 rounded text-sm text-rose-700">
            {error}
          </div>
        )}

        {/* State-driven body */}
        {uiState === 'loading' && (
          <div className="py-12 flex items-center justify-center text-slate-400">
            <LoaderCircle className="w-5 h-5 animate-spin" />
          </div>
        )}

        {uiState === 'gated' && (
          <div className="mt-6 px-4 py-12 text-center border border-dashed border-slate-200 rounded-lg">
            <p className="text-sm text-slate-600">
              {submissionId
                ? 'This submission is the first sequence in the project — there is no predecessor to diff against.'
                : 'Focus a submission to scan for renames. Renames compares the focused submission against its previous sequence.'}
            </p>
          </div>
        )}

        {uiState === 'landing' && (
          <div className="mt-6 px-4 py-12 text-center border border-dashed border-slate-200 rounded-lg">
            <p className="text-sm text-slate-600 mb-4">
              No rename-consistency scan has run for this submission yet.
            </p>
            <button
              onClick={startScan}
              disabled={!canRunScans || scanBusy}
              className={`inline-flex items-center gap-2 px-4 py-2 bg-brand-orange text-white rounded-lg text-sm font-medium hover:bg-brand-orange/90 transition-colors ${!canRunScans || scanBusy ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <RefreshCw className="w-4 h-4" /> Scan now
            </button>
          </div>
        )}

        {uiState === 'scanning' && (
          <ScanProgress
            state={taskState}
            onCancel={cancelScan}
            featureLabel="rename consistency"
            itemLabel="document"
          />
        )}

        {uiState === 'results' && data && (
          <>
            {/* A2F-816: surface when the descriptive (LLM) phase didn't run
                cleanly, so missing descriptive findings have a visible reason
                rather than being silently absent (A2F-822). */}
            {data.descriptiveStatus && data.descriptiveStatus.state !== 'ok' && (
              <div className="mt-4 px-3 py-2 rounded-md border border-amber-200 bg-amber-50/50 text-xs text-amber-800">
                {data.descriptiveStatus.state === 'skipped_budget' && (
                  <>Descriptive rename detection was skipped this scan: {data.descriptiveStatus.candidates} candidate phrases exceeded the per-scan budget of {data.descriptiveStatus.budget}. Identifier renames below are unaffected.</>
                )}
                {data.descriptiveStatus.state === 'partial' && (
                  <>Descriptive rename detection partially completed: {data.descriptiveStatus.erroredDocs} document{data.descriptiveStatus.erroredDocs === 1 ? '' : 's'} failed the referent-check ({data.descriptiveStatus.reason ?? 'error'}{data.descriptiveStatus.doc ? `, e.g. ${data.descriptiveStatus.doc}` : ''}). Other documents' descriptive renames are shown; re-run to retry the rest. Identifier renames are unaffected.</>
                )}
                {data.descriptiveStatus.state === 'error' && (
                  <>Descriptive rename detection could not complete (referent-check {data.descriptiveStatus.reason ?? 'error'}{data.descriptiveStatus.doc ? ` in ${data.descriptiveStatus.doc}` : ''}). Re-run to retry. Identifier renames below are unaffected.</>
                )}
                {data.descriptiveStatus.state === 'skipped_no_llm' && (
                  <>Descriptive rename detection was skipped (no LLM provider configured). Identifier renames below are unaffected.</>
                )}
              </div>
            )}

            {/* Summary line — visible vs total counts so the impact of
                an active search/status filter is obvious. Counts use
                ``appliedRenames`` (post-A2F-982 filter), matching what
                the user actually sees. */}
            <div className="mt-5 flex items-center justify-end border-b border-slate-200 pb-3">
              <p className="text-xs text-slate-500">
                {visibleClusters.length === appliedRenames.length ? (
                  <>
                    {appliedRenames.length}{' '}
                    {appliedRenames.length === 1 ? 'cluster' : 'clusters'}
                  </>
                ) : (
                  <>
                    Showing {visibleClusters.length} of {appliedRenames.length}
                    {' '}{appliedRenames.length === 1 ? 'cluster' : 'clusters'}
                  </>
                )}
                {' '}&middot;{' '}
                <span className={summary.inconsistent > 0 ? 'text-rose-600 font-medium' : ''}>
                  {summary.inconsistent} inconsistent
                </span>
              </p>
            </div>

            {/* Cluster list */}
            {visibleClusters.length === 0 ? (
              <div className="mt-6 px-4 py-12 text-center border border-dashed border-slate-200 rounded-lg">
                <p className="text-sm text-slate-500">
                  {appliedRenames.length === 0
                    ? 'No renames detected between these sequences.'
                    : 'No renames match the current search or status filter.'}
                </p>
              </div>
            ) : (
              <ul className="mt-3 divide-y divide-slate-100">
                {visibleClusters.map((cluster) => (
                  <ClusterRow
                    key={cluster.renameId}
                    cluster={cluster}
                    expanded={expanded.has(cluster.renameId)}
                    onToggle={() => toggleExpanded(cluster.renameId)}
                    canDismiss={canDismissFindings}
                    onDismiss={handleDismiss}
                    onUndismiss={handleUndismiss}
                    onDismissInstance={handleDismissInstance}
                    onUndismissInstance={handleUndismissInstance}
                  />
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cluster row
// ---------------------------------------------------------------------------

interface ClusterRowProps {
  cluster: RenameConsistencyCluster;
  expanded: boolean;
  onToggle: () => void;
  canDismiss: boolean;
  onDismiss: (sourceId: string, reason: string) => Promise<void>;
  onUndismiss: (sourceId: string) => Promise<void>;
  onDismissInstance: (sourceId: string, loc: RenameLocation, reason: string) => Promise<void>;
  onUndismissInstance: (sourceId: string, loc: RenameLocation) => Promise<void>;
}

function ClusterRow({
  cluster, expanded, onToggle, canDismiss, onDismiss, onUndismiss,
  onDismissInstance, onUndismissInstance,
}: ClusterRowProps) {
  const Chevron = expanded ? ChevronDown : ChevronRight;
  const { variants } = cluster;
  const single = variants.length === 1;
  const apps = cluster.totalApplicationCount;
  // A2F-816: descriptive (LLM-confirmed phrase) renames render distinctly —
  // not monospace (they're prose, not identifiers), no stale/straggler count
  // (free text has no meaningful "remaining old id"), and an evidence line.
  const isDescriptive = cluster.kind === 'descriptive';

  // Dismiss / restore state — mirrors the AbbreviationRow control.
  const [dismissing, setDismissing] = useState(false);
  const [dismissReason, setDismissReason] = useState('');
  const [busy, setBusy] = useState(false);

  const confirmDismiss = async () => {
    setBusy(true);
    try {
      await onDismiss(cluster.oldId, dismissReason);
      setDismissing(false);
      setDismissReason('');
    } finally {
      setBusy(false);
    }
  };

  const confirmUndismiss = async () => {
    setBusy(true);
    try {
      await onUndismiss(cluster.oldId);
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className={`py-3 ${cluster.dismissed ? 'opacity-50' : ''}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-start justify-between gap-4 text-left hover:bg-slate-50 px-2 py-1 -mx-2 rounded"
      >
        <div className="flex items-start gap-2 min-w-0">
          <Chevron className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
          <div className="min-w-0">
            {/* A2F-872: source identifier is the cluster header. With a
                single target we keep the familiar ``old → new`` line; with
                several we summarise the variant count and list them below.
                Descriptive renames are prose, so they drop the monospace.

                A2F-982: bold the SOURCE (old id) uniformly across single,
                multi-variant, and descriptive clusters. The user's task is
                find-old / replace-with-new — bolding the source makes the
                list scannable in task order. (Pre-A2F-982 the single-variant
                case bolded the new id; the inconsistency read as random.)
                The Descriptive badge from A2F-816 was removed too — the
                evidence line below already carries the "LLM-confirmed
                phrase rename" signal without an extra label. */}
            {/* Source-emphasis weight is conditional on font (A2F-982 followup):
                ``font-semibold`` (600) reads as visibly bold in monospace
                (identifier clusters) but barely registers in Public Sans
                (descriptive clusters). ``font-bold`` (700) brings the
                descriptive source up to the same perceived weight. */}
            <p className={`${isDescriptive ? 'text-sm' : 'font-mono text-sm'} text-brand-charcoal`}>
              {single ? (
                <>
                  <span className={isDescriptive ? 'font-bold' : 'font-semibold'}>
                    {cluster.oldId}
                  </span>{' '}
                  <span className="text-slate-400">&rarr;</span>{' '}
                  {variants[0].newId}
                </>
              ) : (
                <>
                  <span className={isDescriptive ? 'font-bold' : 'font-semibold'}>
                    {cluster.oldId}
                  </span>{' '}
                  <span className="text-slate-400">
                    &rarr; {cluster.variantCount} new {isDescriptive ? 'phrases' : 'identifiers'}
                  </span>
                </>
              )}
            </p>
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-sm text-slate-600 tabular-nums">
            {apps} {apps === 1 ? 'application' : 'applications'}
            {!single && (
              <span className="text-slate-400">
                {' '}across {cluster.variantCount} variants
              </span>
            )}
          </p>
          {/* Stale/straggler count is identifier-only — descriptive renames
              have no meaningful "old id still appears" notion (A2F-816). */}
          {!isDescriptive && (
            <p
              className={`text-[11px] tabular-nums ${
                cluster.isInconsistent ? 'text-rose-600 font-medium' : 'text-slate-400'
              }`}
            >
              {cluster.remainingOldCount} stale
            </p>
          )}
        </div>
      </button>

      {expanded && (
        <div className="mt-3 ml-6 space-y-3">
          {/* A2F-887: suspected typo / near-miss pairs among this cluster's
              identifiers. Neutral, factual — distinct from the truncation
              ("incomplete") signal; these read as "possibly wrong, verify". */}
          {(cluster.typoSuspected?.length ?? 0) > 0 && (
            <div className="border border-amber-200 bg-amber-50/40 rounded-md p-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-amber-700 mb-1">
                Possible typo{cluster.typoSuspected!.length === 1 ? '' : 's'}
              </p>
              <ul className="space-y-0.5">
                {cluster.typoSuspected!.map((t) => (
                  <li key={`${t.a}-${t.b}`} className="text-xs text-slate-700 font-mono">
                    {t.a} <span className="text-slate-400">↔</span> {t.b}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Renamed-to: one block per variant target, each with its own
              application count + locations. Ordered by the service
              (application count desc, then new id). */}
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Renamed to
            </p>
            {variants.map((v) => (
              <div key={v.newId} className="space-y-1">
                {/* A2F-816: the referent-check's reason for judging the two
                    phrases the same entity. Neutral, factual — no score. */}
                {isDescriptive && v.evidence && (
                  <p className="text-xs text-violet-700/90 italic">
                    Same entity: {v.evidence}
                  </p>
                )}
                <LocationsBlock
                  title={`→ ${v.newId} — ${v.applicationCount} ${v.applicationCount === 1 ? 'application' : 'applications'}${v.introducedIn.ectdSection ? ` (introduced in ${v.introducedIn.ectdSection})` : ''}`}
                  locations={v.newIdLocations}
                  tone="neutral"
                />
              </div>
            ))}
          </div>
          {cluster.oldIdLocations.length > 0 && (
            <LocationsBlock
              title={`Old id (${cluster.oldId}) still appears in ${cluster.oldIdLocations.length} ${cluster.oldIdLocations.length === 1 ? 'location' : 'locations'}`}
              locations={cluster.oldIdLocations}
              tone="warn"
              dismissable
              canDismiss={canDismiss}
              onDismiss={(loc, reason) => onDismissInstance(cluster.oldId, loc, reason)}
              onUndismiss={(loc) => onUndismissInstance(cluster.oldId, loc)}
            />
          )}

          {/* Dismiss / Restore — cluster-level, keyed on the source id.
              Mirrors the AbbreviationRow control exactly. */}
          {cluster.dismissed ? (
            <div className="pt-2 border-t border-slate-100 space-y-2">
              {cluster.dismissReason && (
                <p className="text-xs text-slate-500">
                  <span className="font-medium">Dismiss reason:</span> {cluster.dismissReason}
                </p>
              )}
              {/* No OverrideHistory here: the audit trail is keyed by the
                  dismissal row PK, which the frontend never receives (the
                  dismiss endpoint returns only {status, source_id} and the
                  scan payload carries no dismissal id). Rendering it with an
                  empty id is dead UI. Real wiring tracked in A2F-886. */}
              <button
                onClick={confirmUndismiss}
                disabled={busy || !canDismiss}
                title={canDismiss ? undefined : 'Requires Contributor role'}
                className={`px-3 py-1.5 text-xs font-medium text-brand-orange border border-brand-orange/30 rounded-lg hover:bg-orange-50 transition-colors disabled:opacity-50 ${!canDismiss ? 'cursor-not-allowed' : ''}`}
              >
                {busy ? 'Restoring…' : 'Restore'}
              </button>
            </div>
          ) : dismissing ? (
            <div className="pt-2 border-t border-slate-100 space-y-2">
              <textarea
                placeholder="Reason for dismissal (required, minimum 10 characters)"
                value={dismissReason}
                onChange={(e) => setDismissReason(e.target.value)}
                rows={2}
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange resize-none"
              />
              <p className={`text-xs ${dismissReason.trim().length >= 10 ? 'text-green-500' : 'text-slate-400'}`}>
                {dismissReason.trim().length}/10 characters
              </p>
              <div className="flex gap-2">
                <button
                  onClick={confirmDismiss}
                  disabled={busy || dismissReason.trim().length < 10}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-brand-orange rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50"
                >
                  {busy ? 'Dismissing…' : 'Confirm'}
                </button>
                <button
                  onClick={() => { setDismissing(false); setDismissReason(''); }}
                  className="px-3 py-1.5 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="pt-2 border-t border-slate-100">
              <button
                onClick={() => setDismissing(true)}
                disabled={!canDismiss}
                title={canDismiss ? undefined : 'Requires Contributor role'}
                className={`px-3 py-1.5 text-xs font-medium text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors ${!canDismiss ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Dismiss
              </button>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Locations block — list of doc + section + snippet rows with click-to-open
// ---------------------------------------------------------------------------

interface LocationsBlockProps {
  title: string;
  locations: RenameLocation[];
  tone: 'neutral' | 'warn';
  // A2F-898: per-instance dismissal — enabled only for the old-id (stale)
  // block. When set, each row gets a dismiss/restore control and the block
  // hides dismissed rows behind a "Hide dismissed (N)" toggle (default on),
  // mirroring the cluster-level control + the Abbreviations pattern.
  dismissable?: boolean;
  canDismiss?: boolean;
  onDismiss?: (loc: RenameLocation, reason: string) => Promise<void>;
  onUndismiss?: (loc: RenameLocation) => Promise<void>;
}

const MAX_LOCATIONS_RENDERED = 12;

function LocationsBlock({
  title, locations, tone, dismissable, canDismiss, onDismiss, onUndismiss,
}: LocationsBlockProps) {
  const [showAll, setShowAll] = useState(false);
  const [hideDismissed, setHideDismissed] = useState(true);

  const dismissedCount = dismissable
    ? locations.filter((l) => l.dismissed).length
    : 0;
  const filtered = dismissable && hideDismissed
    ? locations.filter((l) => !l.dismissed)
    : locations;
  const shown = showAll ? filtered : filtered.slice(0, MAX_LOCATIONS_RENDERED);
  const remaining = filtered.length - shown.length;

  return (
    <div
      className={`border rounded-md p-3 ${
        tone === 'warn'
          ? 'border-rose-200 bg-rose-50/30'
          : 'border-slate-200 bg-slate-50/40'
      }`}
    >
      <div className="flex items-center justify-between mb-2 gap-2">
        <p
          className={`text-xs font-semibold uppercase tracking-wider ${
            tone === 'warn' ? 'text-rose-700' : 'text-slate-500'
          }`}
        >
          {title}
        </p>
        {dismissable && dismissedCount > 0 && (
          <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer select-none shrink-0">
            <input
              type="checkbox"
              checked={hideDismissed}
              onChange={(e) => setHideDismissed(e.target.checked)}
              className="rounded border-slate-300 text-brand-orange focus:ring-brand-orange/30"
            />
            <EyeOff className="w-3 h-3 text-slate-400" />
            Hide dismissed ({dismissedCount})
          </label>
        )}
      </div>
      <ul className="space-y-1.5">
        {shown.map((loc, i) => (
          <StaleRow
            key={`${loc.documentId}-${loc.matchIndex}-${loc.contextHash ?? i}`}
            loc={loc}
            dismissable={!!dismissable}
            canDismiss={!!canDismiss}
            onDismiss={onDismiss}
            onUndismiss={onUndismiss}
          />
        ))}
      </ul>
      {remaining > 0 && (
        <button
          onClick={() => setShowAll(true)}
          className="mt-2 text-xs text-brand-orange hover:underline"
        >
          Show {remaining} more
        </button>
      )}
    </div>
  );
}

function StaleRow({
  loc, dismissable, canDismiss, onDismiss, onUndismiss,
}: {
  loc: RenameLocation;
  dismissable: boolean;
  canDismiss: boolean;
  onDismiss?: (loc: RenameLocation, reason: string) => Promise<void>;
  onUndismiss?: (loc: RenameLocation) => Promise<void>;
}) {
  const [dismissing, setDismissing] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const confirmDismiss = async () => {
    if (!onDismiss) return;
    setBusy(true);
    try {
      await onDismiss(loc, reason);
      setDismissing(false);
      setReason('');
    } finally {
      setBusy(false);
    }
  };
  const confirmRestore = async () => {
    if (!onUndismiss) return;
    setBusy(true);
    try {
      await onUndismiss(loc);
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className={`text-xs text-slate-700 ${loc.dismissed ? 'opacity-50' : ''}`}>
      <div className="flex items-start gap-2">
        <button
          onClick={() => api.documents.openLocal(loc.documentId).catch(() => {})}
          className="font-medium text-brand-orange hover:underline shrink-0"
          title={`Open ${loc.fileName}`}
        >
          {formatSectionLabel(loc.fileName)}
        </button>
        <span className="text-slate-500 italic truncate">
          &ldquo;{loc.snippet.trim()}&rdquo;
        </span>
        {dismissable && canDismiss && !loc.dismissed && !dismissing && (
          <button
            onClick={() => setDismissing(true)}
            className="ml-auto shrink-0 text-slate-400 hover:text-slate-600"
            title="Dismiss this reference"
          >
            Dismiss
          </button>
        )}
        {dismissable && loc.dismissed && (
          <button
            onClick={confirmRestore}
            disabled={busy || !canDismiss}
            title={canDismiss ? undefined : 'Requires Contributor role'}
            className={`ml-auto shrink-0 text-brand-orange hover:underline ${!canDismiss ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {busy ? 'Restoring…' : 'Restore'}
          </button>
        )}
      </div>
      {loc.dismissed && loc.dismissReason && (
        <p className="text-[11px] text-slate-400 mt-0.5 ml-1">
          Dismiss reason: {loc.dismissReason}
        </p>
      )}
      {dismissing && (
        <div className="mt-1.5 ml-1 space-y-1.5">
          <textarea
            placeholder="Reason for dismissal (required, minimum 10 characters)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            className="w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange resize-none"
          />
          <p className={`text-[11px] ${reason.trim().length >= 10 ? 'text-green-500' : 'text-slate-400'}`}>
            {reason.trim().length}/10 characters
          </p>
          <div className="flex gap-2">
            <button
              onClick={confirmDismiss}
              disabled={busy || reason.trim().length < 10}
              className="px-2.5 py-1 text-[11px] font-medium text-white bg-brand-orange rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50"
            >
              {busy ? 'Dismissing…' : 'Confirm'}
            </button>
            <button
              onClick={() => { setDismissing(false); setReason(''); }}
              className="px-2.5 py-1 text-[11px] font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </li>
  );
}
